import React, {useEffect, useMemo, useState, FormEvent} from 'react';
import "./AuthActions.css";
import {Helmet} from "react-helmet";
import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import {
    applyActionCode,
    checkActionCode,
    confirmPasswordReset,
    verifyPasswordResetCode,
    Auth,
    getAuth
} from 'firebase/auth';
import LoadingSpinner from "../../components/loading-spinner/LoadingSpinner";

function AuthActions() {
    const { mode, oobCode, apiKey, status} = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return {
            mode: params.get('mode') as Mode,
            oobCode: params.get('oobCode') || '',
            apiKey: params.get('apiKey') || '',
            continueUrl: params.get('continueUrl') || '',
            lang: params.get('lang') || 'en',
            status: params.get('status') || ''
        };
    }, []);

    const app: FirebaseApp | null = useMemo(() => {
        if (!apiKey) return null;
        const existing = getApps().find(a => (a.options as any)?.apiKey === apiKey);
        if (existing) return existing;
        return initializeApp({ apiKey });
    }, [apiKey]);

    const dynamicAuth: Auth | null = useMemo(() => app ? getAuth(app) : null, [app]);

    const content = useMemo(() => {
        if (status !== '') {
            switch (mode) {
                case 'resetPassword':
                    return (
                        <>
                            <h1>Skift adgangskode</h1>
                            {status === 'success' &&
                                <p>Adgangskoden er nu ændret! Du kan nu logge ind i appen</p>
                            }
                        </>
                    )
                default:
                    return <p>Succes! Du kan lukke denne side</p>;
            }
        }
        if (!dynamicAuth) return <MissingApiKey />;
        switch (mode) {
            case 'resetPassword':
                return <ResetPassword code={oobCode} auth={dynamicAuth} />;
            case 'verifyEmail':
                return <VerifyEmail code={oobCode} auth={dynamicAuth} />;
            case 'recoverEmail':
                return <RecoverEmail code={oobCode} auth={dynamicAuth} />;
            default:
                return <Unknown />;
        }
    }, [mode, oobCode, dynamicAuth, status]);

    if (!mode || (!oobCode && status === '')) {
        window.location.replace('/');
        return null;
    }

    return (
        <>
            <Helmet>
                <meta name="robots" content="noindex, nofollow"/>
            </Helmet>
            <div className="container auth-actions">
                {content}
            </div>
        </>
    );
}

const MissingApiKey: React.FC = () => (
    <div>
        <h1>Fejl</h1>
        <p>Der skete en teknisk fejl. Kontakt os venligst hvis det fortsætter.</p>
    </div>
);

// Reset Password Component
const ResetPassword: React.FC<{ code: string; auth: Auth }> = ({ code, auth }) => {
    const [email, setEmail] = useState<string | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [status, setStatus] = useState<'idle'|'loading'|'verified'|'success'|'error'>('idle');
    const [error, setError] = useState<string | null>(null);
    const [isRetryableError, setIsRetryableError] = useState(true);

    useEffect(() => {
        if (!code) return;
        let active = true;
        setStatus('loading');
        verifyPasswordResetCode(auth, code)
            .then(mail => { if (active) { setEmail(mail); setStatus('verified'); }})
            .catch(e => { if (active) { 
                setError(mapAuthError(e)); 
                setIsRetryableError(mapRetryableError(e));
                setStatus('error');
            }});
        return () => { active = false; };
    }, [code, auth]);

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) { 
            setError('Adgangskoderne er ikke ens');
            setStatus('error');
            return; 
        }
        if (!code) { setError('Manglende kode'); return; }
        setStatus('loading');
        confirmPasswordReset(auth, code, newPassword)
            .then(() => {
                // Navigate to same page with status=success (remove oobCode)
                const url = new URL(window.location.href);
                url.searchParams.set('status', 'success');
                url.searchParams.delete('oobCode');
                // Preserve apiKey and mode already present
                window.location.replace(url.toString());
            })
            .catch(e => { setError(mapAuthError(e)); setStatus('error'); });
    };

    return (
        <>
            <h1>Skift adgangskode</h1>
            {status === 'loading' &&
                <div className="spinner-container">
                    <LoadingSpinner />
                </div>
            }
            {status === 'success' && <p>Adgangskoden er nu ændret! Du kan nu logge ind i appen.</p>}
            {!isRetryableError && status === 'error' && (
                <p className="error">{error}</p>
            )}
            {status !== 'success' && status !== 'loading' && isRetryableError && (
                <form className="auth-form" onSubmit={onSubmit}>
                    {email && <p>Vælg en ny adgangskode til din konto</p>}
                    {email && (
                        <input
                            className="saelv-text-input disabled"
                            disabled={true}
                            type="email"
                            name="email"
                            autoComplete="username"
                            value={email}
                            readOnly
                            tabIndex={-1}
                        />
                    )}
                    <input
                        className="saelv-text-input"
                        type="password"
                        placeholder="Ny adgangskode"
                        autoComplete="new-password"
                        required
                        minLength={6}
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                    />
                    <input
                        className="saelv-text-input"
                        type="password"
                        placeholder="Gentag adgangskode"
                        autoComplete="new-password"
                        required
                        minLength={6}
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                    />
                    {status === 'error' && <p className="auth-error-text">{error}</p>}
                    <button
                        className="saelv-button prominent"
                        type="submit"
                        disabled={newPassword.length < 6 || confirmPassword.length < 6}>
                        Skift adgangskode
                    </button>
                </form>
            )}
        </>
    );
};

// Verify Email Component
const VerifyEmail: React.FC<{ code: string; auth: Auth }> = ({ code, auth }) => {
    const [status, setStatus] = useState<'idle'|'loading'|'success'|'error'>('idle');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!code) return;
        setStatus('loading');
        applyActionCode(auth, code)
            .then(() => setStatus('success'))
            .catch(e => { setError(mapAuthError(e)); setStatus('error'); });
    }, [code, auth]);

    return (
        <div>
            <h1>Bekræft din mail</h1>
            {status === 'loading' &&
                <div className="spinner-container">
                    <LoadingSpinner />
                </div>
            }
            {status === 'success' && <p>Din mail er nu bekræftet. Du kan lukke denne side.</p>}
            {status === 'error' && <p className="error">{error}</p>}
        </div>
    );
};

// Recover Email Component
const RecoverEmail: React.FC<{ code: string; auth: Auth }> = ({ code, auth }) => {
    const [restored, setRestored] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<{fromEmail?: string; toEmail?: string}>({});
    const [status, setStatus] = useState<'idle'|'loading'|'done'|'error'>('idle');

    useEffect(() => {
        if (!code) return;
        setStatus('loading');
        checkActionCode(auth, code)
            .then(result => {
                const data: any = result.data;
                setInfo({ fromEmail: data?.fromEmail, toEmail: data?.email });
                // Apply immediately (auto-recover)
                return applyActionCode(auth, code);
            })
            .then(() => { setRestored(true); setStatus('done'); })
            .catch(e => { setError(mapAuthError(e)); setStatus('error'); });
    }, [code, auth]);

    return (
        <div>
            <h1>Gendan din mail</h1>
            {status === 'loading' && <p>Arbejder…</p>}
            {status === 'done' && restored && (
                <p>Din mail er gendannet{info.fromEmail ? `: ${info.fromEmail}` : ''}. Du kan nu logge ind igen.</p>
            )}
            {status === 'error' && <p className="error">{error}</p>}
        </div>
    );
};

const Unknown: React.FC = () => {
    useEffect(() => {
        window.location.replace('/');
    }, []);
    return null;
};

function mapAuthError(e: any): string {
    const code: string = e?.code || '';
    switch (code) {
        case 'auth/expired-action-code': return 'Linket er udløbet.';
        case 'auth/invalid-action-code': return 'Linket er ugyldigt eller allerede brugt.';
        case 'auth/user-disabled': return 'Brugeren er deaktiveret.';
        case 'auth/user-not-found': return 'Brugeren findes ikke.';
        case 'auth/weak-password': return 'Adgangskoden er for svag (minimum 6 tegn).';
        default: return 'Der opstod en fejl. Prøv igen.';
    }
}

function mapRetryableError(e: any): boolean {
    const code: string = e?.code || '';
    switch (code) {
        case 'auth/weak-password':
            return true;
        default:
            return false;
    }
}

type Mode =
    | 'resetPassword'
    | 'verifyEmail'
    | 'recoverEmail'
    | string
    | null;

export default AuthActions;